import React, { useState } from 'react';
import { Button } from './ui/button';
import { Card } from './ui/card';
import { Badge } from './ui/badge';
import { Textarea } from './ui/textarea';
import { Input } from './ui/input';
import {
  CheckCircle,
  XCircle,
  Edit3,
  DollarSign,
  FileText,
  Calendar,
  User,
  MessageSquare,
  Clock,
  AlertCircle,
  Send,
  Loader,
} from 'lucide-react';
import { toast } from 'sonner';
import api from '../utils/firebase/api';

interface InvoiceApprovalProps {
  invoice: any;
  currentUser: { id: string; name: string; role: string };
  onUpdate: () => void;
  onPreview: () => void;
}

export default function InvoiceApproval({
  invoice,
  currentUser,
  onUpdate,
  onPreview,
}: InvoiceApprovalProps) {
  const [showApprove, setShowApprove] = useState(false);
  const [showReject, setShowReject] = useState(false);
  const [showRevision, setShowRevision] = useState(false);
  const [showPayment, setShowPayment] = useState(false);
  const [showComment, setShowComment] = useState(false);

  // Form states
  const [approvalNotes, setApprovalNotes] = useState('');
  const [rejectionReason, setRejectionReason] = useState('');
  const [rejectionNotes, setRejectionNotes] = useState('');
  const [revisionReason, setRevisionReason] = useState('');
  const [revisionDetails, setRevisionDetails] = useState('');
  const [paymentMethod, setPaymentMethod] = useState('');
  const [paymentReference, setPaymentReference] = useState('');
  const [paymentNotes, setPaymentNotes] = useState('');
  const [commentText, setCommentText] = useState('');
  const [loading, setLoading] = useState(false);

  const canApprove = currentUser.role === 'admin' || currentUser.role === 'manager';
  const canPay = currentUser.role === 'admin';
  const approvalStatus = invoice.approvalStatus || 'pending';
  const paymentStatus = invoice.paymentStatus || 'unpaid';

  const handleApprove = async () => {
    if (!canApprove) {
      toast.error('You do not have permission to approve invoices');
      return;
    }

    setLoading(true);
    try {
      await api.approveInvoice(invoice.id, {
        approvedBy: currentUser.name,
        notes: approvalNotes,
      });
      
      toast.success('Invoice approved successfully', {
        description: 'Email notification sent to service provider',
      });
      setShowApprove(false);
      setApprovalNotes('');
      onUpdate();
    } catch (error) {
      console.error('Approval error:', error);
      toast.error('Failed to approve invoice');
    } finally {
      setLoading(false);
    }
  };

  const handleReject = async () => {
    if (!canApprove) {
      toast.error('You do not have permission to reject invoices');
      return;
    }

    if (!rejectionReason.trim()) {
      toast.error('Please provide a rejection reason');
      return;
    }

    setLoading(true);
    try {
      await api.updateInvoice(invoice.id, {
        approvalStatus: 'rejected',
        rejectedBy: currentUser.name,
        rejectionReason,
        rejectionNotes,
        rejectedAt: new Date().toISOString(),
      });
      
      toast.success('Invoice rejected', {
        description: 'Email notification sent to service provider',
      });
      setShowReject(false);
      setRejectionReason('');
      setRejectionNotes('');
      onUpdate();
    } catch (error) {
      console.error('Rejection error:', error);
      toast.error('Failed to reject invoice');
    } finally {
      setLoading(false);
    }
  };

  const handleRequestRevision = async () => {
    if (!canApprove) {
      toast.error('You do not have permission to request revisions');
      return;
    }

    if (!revisionReason.trim()) {
      toast.error('Please provide a revision reason');
      return;
    }

    setLoading(true);
    try {
      await api.updateInvoice(invoice.id, {
        approvalStatus: 'revision_requested',
        revisionRequestedBy: currentUser.name,
        revisionReason,
        revisionDetails,
        revisionRequestedAt: new Date().toISOString(),
      });
      
      toast.success('Revision requested', {
        description: 'Email notification sent to service provider',
      });
      setShowRevision(false);
      setRevisionReason('');
      setRevisionDetails('');
      onUpdate();
    } catch (error) {
      console.error('Revision error:', error);
      toast.error('Failed to request revision');
    } finally {
      setLoading(false);
    }
  };

  const handleMarkPaid = async () => {
    if (!canPay) {
      toast.error('You do not have permission to mark invoices as paid');
      return;
    }

    if (approvalStatus !== 'approved') {
      toast.error('Invoice must be approved before marking as paid');
      return;
    }

    if (!paymentMethod.trim()) {
      toast.error('Please specify payment method');
      return;
    }

    setLoading(true);
    try {
      await api.updateInvoice(invoice.id, {
        paymentStatus: 'paid',
        paidBy: currentUser.name,
        paymentMethod,
        paymentReference,
        paymentNotes,
        paidAt: new Date().toISOString(),
      });
      
      toast.success('Invoice marked as paid', {
        description: 'Email notification sent to service provider',
      });
      setShowPayment(false);
      setPaymentMethod('');
      setPaymentReference('');
      setPaymentNotes('');
      onUpdate();
    } catch (error) {
      console.error('Payment error:', error);
      toast.error('Failed to mark invoice as paid');
    } finally {
      setLoading(false);
    }
  };

  const handleAddComment = async () => {
    if (!commentText.trim()) {
      toast.error('Please enter a comment');
      return;
    }

    setLoading(true);
    try {
      const comments = invoice.comments || [];
      comments.push({
        author: currentUser.name,
        text: commentText,
        timestamp: new Date().toISOString(),
      });
      
      await api.updateInvoice(invoice.id, { comments });
      
      toast.success('Comment added');
      setShowComment(false);
      setCommentText('');
      onUpdate();
    } catch (error) {
      console.error('Comment error:', error);
      toast.error('Failed to add comment');
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadge = () => {
    switch (approvalStatus) {
      case 'approved':
        return (
          <Badge className="bg-green-100 text-green-800">
            <CheckCircle className="h-3 w-3 mr-1" />
            Approved
          </Badge>
        );
      case 'rejected':
        return (
          <Badge className="bg-red-100 text-red-800">
            <XCircle className="h-3 w-3 mr-1" />
            Rejected
          </Badge>
        );
      case 'revision-requested':
        return (
          <Badge className="bg-orange-100 text-orange-800">
            <Edit3 className="h-3 w-3 mr-1" />
            Revision Requested
          </Badge>
        );
      default:
        return (
          <Badge className="bg-yellow-100 text-yellow-800">
            <Clock className="h-3 w-3 mr-1" />
            Pending Review
          </Badge>
        );
    }
  };

  const getPaymentBadge = () => {
    if (paymentStatus === 'paid') {
      return (
        <Badge className="bg-blue-100 text-blue-800">
          <DollarSign className="h-3 w-3 mr-1" />
          Paid
        </Badge>
      );
    }
    return (
      <Badge className="bg-gray-100 text-gray-800">
        <DollarSign className="h-3 w-3 mr-1" />
        Unpaid
      </Badge>
    );
  };

  return (
    <div className="space-y-4">
      {/* Invoice Summary Card */}
      <Card className="p-6 border-2">
        <div className="flex items-start justify-between mb-4">
          <div>
            <div className="flex items-center space-x-2 mb-2">
              <h3 className="text-xl font-semibold text-gray-900">
                Invoice #{invoice.invoiceNumber}
              </h3>
              {getStatusBadge()}
              {getPaymentBadge()}
            </div>
            <p className="text-3xl font-bold text-blue-600">
              ${invoice.amount?.toFixed(2)}
            </p>
          </div>
          <Button onClick={onPreview} variant="outline">
            <FileText className="h-4 w-4 mr-2" />
            View Invoice
          </Button>
        </div>

        <div className="grid grid-cols-2 gap-4 mb-4">
          <div>
            <p className="text-sm text-gray-600 mb-1">Service Provider</p>
            <p className="font-medium text-gray-900">{invoice.uploadedBy}</p>
          </div>
          <div>
            <p className="text-sm text-gray-600 mb-1">Upload Date</p>
            <p className="font-medium text-gray-900">
              {new Date(invoice.uploadedAt).toLocaleDateString()}
            </p>
          </div>
          <div>
            <p className="text-sm text-gray-600 mb-1">File Name</p>
            <p className="font-medium text-gray-900 truncate">{invoice.fileName}</p>
          </div>
          <div>
            <p className="text-sm text-gray-600 mb-1">Ticket ID</p>
            <p className="font-medium text-gray-900">{invoice.ticketId}</p>
          </div>
        </div>

        {invoice.notes && (
          <div className="bg-gray-50 rounded-lg p-3 mb-4">
            <p className="text-sm text-gray-600 mb-1">Notes from Provider:</p>
            <p className="text-gray-900">{invoice.notes}</p>
          </div>
        )}

        {/* Action Buttons */}
        {canApprove && (
          <div className="flex flex-wrap gap-2 pt-4 border-t">
            {approvalStatus === 'pending' || approvalStatus === 'revision-requested' ? (
              <>
                <Button
                  onClick={() => setShowApprove(true)}
                  className="bg-green-600 hover:bg-green-700"
                >
                  <CheckCircle className="h-4 w-4 mr-2" />
                  Approve
                </Button>
                <Button
                  onClick={() => setShowReject(true)}
                  variant="destructive"
                >
                  <XCircle className="h-4 w-4 mr-2" />
                  Reject
                </Button>
                <Button
                  onClick={() => setShowRevision(true)}
                  variant="outline"
                >
                  <Edit3 className="h-4 w-4 mr-2" />
                  Request Revision
                </Button>
              </>
            ) : null}

            {approvalStatus === 'approved' && paymentStatus !== 'paid' && canPay && (
              <Button
                onClick={() => setShowPayment(true)}
                className="bg-blue-600 hover:bg-blue-700"
              >
                <DollarSign className="h-4 w-4 mr-2" />
                Mark as Paid
              </Button>
            )}

            <Button
              onClick={() => setShowComment(true)}
              variant="outline"
            >
              <MessageSquare className="h-4 w-4 mr-2" />
              Add Comment
            </Button>
          </div>
        )}
      </Card>

      {/* Approval Form */}
      {showApprove && (
        <Card className="p-6 border-2 border-green-200 bg-green-50">
          <h4 className="font-semibold text-gray-900 mb-4 flex items-center">
            <CheckCircle className="h-5 w-5 text-green-600 mr-2" />
            Approve Invoice
          </h4>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Approval Notes (Optional)
              </label>
              <Textarea
                value={approvalNotes}
                onChange={(e) => setApprovalNotes(e.target.value)}
                placeholder="Add any notes about this approval..."
                rows={3}
              />
            </div>
            <div className="flex space-x-2">
              <Button
                onClick={handleApprove}
                disabled={loading}
                className="bg-green-600 hover:bg-green-700"
              >
                {loading ? (
                  <Loader className="h-4 w-4 mr-2 animate-spin" />
                ) : (
                  <CheckCircle className="h-4 w-4 mr-2" />
                )}
                Confirm Approval
              </Button>
              <Button
                onClick={() => setShowApprove(false)}
                variant="outline"
                disabled={loading}
              >
                Cancel
              </Button>
            </div>
          </div>
        </Card>
      )}

      {/* Rejection Form */}
      {showReject && (
        <Card className="p-6 border-2 border-red-200 bg-red-50">
          <h4 className="font-semibold text-gray-900 mb-4 flex items-center">
            <XCircle className="h-5 w-5 text-red-600 mr-2" />
            Reject Invoice
          </h4>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Rejection Reason <span className="text-red-500">*</span>
              </label>
              <Input
                value={rejectionReason}
                onChange={(e) => setRejectionReason(e.target.value)}
                placeholder="e.g., Incorrect amount, missing documentation..."
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Additional Notes (Optional)
              </label>
              <Textarea
                value={rejectionNotes}
                onChange={(e) => setRejectionNotes(e.target.value)}
                placeholder="Provide more details..."
                rows={3}
              />
            </div>
            <div className="flex space-x-2">
              <Button
                onClick={handleReject}
                disabled={loading}
                variant="destructive"
              >
                {loading ? (
                  <Loader className="h-4 w-4 mr-2 animate-spin" />
                ) : (
                  <XCircle className="h-4 w-4 mr-2" />
                )}
                Confirm Rejection
              </Button>
              <Button
                onClick={() => setShowReject(false)}
                variant="outline"
                disabled={loading}
              >
                Cancel
              </Button>
            </div>
          </div>
        </Card>
      )}

      {/* Revision Request Form */}
      {showRevision && (
        <Card className="p-6 border-2 border-orange-200 bg-orange-50">
          <h4 className="font-semibold text-gray-900 mb-4 flex items-center">
            <Edit3 className="h-5 w-5 text-orange-600 mr-2" />
            Request Revision
          </h4>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Revision Reason <span className="text-red-500">*</span>
              </label>
              <Input
                value={revisionReason}
                onChange={(e) => setRevisionReason(e.target.value)}
                placeholder="e.g., Need corrected amount, missing signatures..."
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Detailed Instructions
              </label>
              <Textarea
                value={revisionDetails}
                onChange={(e) => setRevisionDetails(e.target.value)}
                placeholder="Explain what needs to be changed..."
                rows={4}
              />
            </div>
            <div className="flex space-x-2">
              <Button
                onClick={handleRequestRevision}
                disabled={loading}
                className="bg-orange-600 hover:bg-orange-700"
              >
                {loading ? (
                  <Loader className="h-4 w-4 mr-2 animate-spin" />
                ) : (
                  <Send className="h-4 w-4 mr-2" />
                )}
                Send Revision Request
              </Button>
              <Button
                onClick={() => setShowRevision(false)}
                variant="outline"
                disabled={loading}
              >
                Cancel
              </Button>
            </div>
          </div>
        </Card>
      )}

      {/* Payment Form */}
      {showPayment && (
        <Card className="p-6 border-2 border-blue-200 bg-blue-50">
          <h4 className="font-semibold text-gray-900 mb-4 flex items-center">
            <DollarSign className="h-5 w-5 text-blue-600 mr-2" />
            Mark Invoice as Paid
          </h4>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Payment Method <span className="text-red-500">*</span>
              </label>
              <Input
                value={paymentMethod}
                onChange={(e) => setPaymentMethod(e.target.value)}
                placeholder="e.g., Check, ACH, Wire Transfer..."
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Payment Reference/Check Number
              </label>
              <Input
                value={paymentReference}
                onChange={(e) => setPaymentReference(e.target.value)}
                placeholder="e.g., Check #12345, Confirmation #..."
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Payment Notes (Optional)
              </label>
              <Textarea
                value={paymentNotes}
                onChange={(e) => setPaymentNotes(e.target.value)}
                placeholder="Add any payment notes..."
                rows={2}
              />
            </div>
            <div className="flex space-x-2">
              <Button
                onClick={handleMarkPaid}
                disabled={loading}
                className="bg-blue-600 hover:bg-blue-700"
              >
                {loading ? (
                  <Loader className="h-4 w-4 mr-2 animate-spin" />
                ) : (
                  <DollarSign className="h-4 w-4 mr-2" />
                )}
                Confirm Payment
              </Button>
              <Button
                onClick={() => setShowPayment(false)}
                variant="outline"
                disabled={loading}
              >
                Cancel
              </Button>
            </div>
          </div>
        </Card>
      )}

      {/* Comment Form */}
      {showComment && (
        <Card className="p-6 border-2 border-gray-200">
          <h4 className="font-semibold text-gray-900 mb-4 flex items-center">
            <MessageSquare className="h-5 w-5 text-gray-600 mr-2" />
            Add Comment
          </h4>
          <div className="space-y-4">
            <Textarea
              value={commentText}
              onChange={(e) => setCommentText(e.target.value)}
              placeholder="Add your comment..."
              rows={3}
            />
            <div className="flex space-x-2">
              <Button onClick={handleAddComment} disabled={loading}>
                {loading ? (
                  <Loader className="h-4 w-4 mr-2 animate-spin" />
                ) : (
                  <Send className="h-4 w-4 mr-2" />
                )}
                Post Comment
              </Button>
              <Button
                onClick={() => setShowComment(false)}
                variant="outline"
                disabled={loading}
              >
                Cancel
              </Button>
            </div>
          </div>
        </Card>
      )}

      {/* Approval History */}
      {invoice.approvalHistory && invoice.approvalHistory.length > 0 && (
        <Card className="p-6">
          <h4 className="font-semibold text-gray-900 mb-4">Approval History</h4>
          <div className="space-y-3">
            {invoice.approvalHistory.map((record: any, index: number) => (
              <div key={index} className="border-l-4 border-blue-600 pl-4 py-2">
                {record.approvedBy && (
                  <>
                    <p className="font-medium text-gray-900">
                      Approved by {record.approvedBy}
                    </p>
                    <p className="text-sm text-gray-600">
                      {new Date(record.approvedAt).toLocaleString()}
                    </p>
                    {record.approvalNotes && (
                      <p className="text-sm text-gray-700 mt-1">{record.approvalNotes}</p>
                    )}
                  </>
                )}
                {record.rejectedBy && (
                  <>
                    <p className="font-medium text-red-700">
                      Rejected by {record.rejectedBy}
                    </p>
                    <p className="text-sm text-gray-600">
                      {new Date(record.rejectedAt).toLocaleString()}
                    </p>
                    <p className="text-sm text-red-600 mt-1">
                      Reason: {record.rejectionReason}
                    </p>
                    {record.rejectionNotes && (
                      <p className="text-sm text-gray-700 mt-1">{record.rejectionNotes}</p>
                    )}
                  </>
                )}
              </div>
            ))}
          </div>
        </Card>
      )}

      {/* Comments Section */}
      {invoice.comments && invoice.comments.length > 0 && (
        <Card className="p-6">
          <h4 className="font-semibold text-gray-900 mb-4">Comments</h4>
          <div className="space-y-3">
            {invoice.comments.map((comment: any) => (
              <div key={comment.id} className="bg-gray-50 rounded-lg p-3">
                <div className="flex items-center justify-between mb-2">
                  <p className="font-medium text-gray-900">{comment.author}</p>
                  <p className="text-xs text-gray-500">
                    {new Date(comment.createdAt).toLocaleString()}
                  </p>
                </div>
                <p className="text-gray-700">{comment.text}</p>
              </div>
            ))}
          </div>
        </Card>
      )}

      {/* Payment Info */}
      {invoice.paymentInfo && (
        <Card className="p-6 bg-green-50 border-2 border-green-200">
          <h4 className="font-semibold text-gray-900 mb-4 flex items-center">
            <DollarSign className="h-5 w-5 text-green-600 mr-2" />
            Payment Information
          </h4>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <p className="text-sm text-gray-600 mb-1">Paid By</p>
              <p className="font-medium text-gray-900">{invoice.paymentInfo.paidBy}</p>
            </div>
            <div>
              <p className="text-sm text-gray-600 mb-1">Payment Date</p>
              <p className="font-medium text-gray-900">
                {new Date(invoice.paymentInfo.paidAt).toLocaleDateString()}
              </p>
            </div>
            <div>
              <p className="text-sm text-gray-600 mb-1">Payment Method</p>
              <p className="font-medium text-gray-900">{invoice.paymentInfo.paymentMethod}</p>
            </div>
            {invoice.paymentInfo.paymentReference && (
              <div>
                <p className="text-sm text-gray-600 mb-1">Reference</p>
                <p className="font-medium text-gray-900">{invoice.paymentInfo.paymentReference}</p>
              </div>
            )}
          </div>
          {invoice.paymentInfo.paymentNotes && (
            <div className="mt-4 pt-4 border-t border-green-200">
              <p className="text-sm text-gray-600 mb-1">Notes</p>
              <p className="text-gray-900">{invoice.paymentInfo.paymentNotes}</p>
            </div>
          )}
        </Card>
      )}
    </div>
  );
}
